import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaCheck, FaEdit, FaPercent, FaTimes, FaDollarSign } from "react-icons/fa";
import Swal from "sweetalert2";
import { updateCommissionInstallment, updateNewCommissionPlan } from "../commissionsApi";
import { mapWithConcurrency } from "../../../../utilis/mapWithConcurrency";
import { formatCurrency, formatPercent } from "./commissionDetailConstants";
import {
  calculateCommissionDiscountMetrics,
} from "./commissionDiscountUtils";
import { buildStudentDiscountPlanPayload } from "./commissionPlanUtils";
import SetupCardSavedBody from "./commissionSetupCardLayout";

const controlClass =
  "h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-800 outline-none transition hover:border-slate-300 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 disabled:cursor-not-allowed disabled:bg-slate-50";

function Chip({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}

function FieldLabel({ children, hint }) {
  return (
    <div className="mb-1 flex items-center justify-between gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {children}
      </span>
      {hint ? <span className="text-[10px] text-slate-400">{hint}</span> : null}
    </div>
  );
}

function toNumberSafe(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function roundMoney(value) {
  return Math.round(toNumberSafe(value) * 100) / 100;
}

function installmentIsEditable(row = {}) {
  const allocs = row.financeAllocations || row.finance_allocations || [];
  if (Array.isArray(allocs) && allocs.length > 0) return false;
  if (row.financeInvoiceId || row.finance_invoice_id) return false;
  const status = String(row.status || "").toLowerCase();
  if (status === "paid" || status === "cancelled" || status === "void") {
    return false;
  }
  return true;
}

export function deriveStudentDiscountSettings(plan = {}) {
  const planType = String(
    plan?.studentDiscountType ?? plan?.student_discount_type ?? "",
  ).toLowerCase();
  const storedAmount = toNumberSafe(
    plan?.studentDiscountAmount ?? plan?.student_discount_amount,
  );
  const commissionable = toNumberSafe(
    plan?.commissionableTuitionFee ?? plan?.commissionable_tuition_fee,
  );

  if (planType === "percentage") {
    const percent = toNumberSafe(
      plan?.studentDiscountPercent ?? plan?.student_discount_percent,
    );
    if (percent > 0) {
      const amount =
        storedAmount > 0
          ? storedAmount
          : commissionable > 0
            ? roundMoney((commissionable * percent) / 100)
            : 0;
      return {
        mode: "percentage",
        value: String(percent),
        amount,
      };
    }
  }

  if (planType === "fixed") {
    const amount = storedAmount;
    if (amount > 0) {
      return { mode: "fixed", value: String(amount), amount };
    }
  }

  return { mode: "percentage", value: "", amount: 0 };
}

function previewStudentDiscountAmount(mode, value, commissionableFee) {
  const numericValue = toNumberSafe(value);
  if (numericValue <= 0) return 0;
  if (mode === "percentage") {
    return commissionableFee > 0
      ? roundMoney((commissionableFee * numericValue) / 100)
      : 0;
  }
  return roundMoney(numericValue);
}

function buildInstallmentDiscountPayload(row, plan, { mode, value, totalCommissionableFee }) {
  const fee = toNumberSafe(row.tuitionAmount ?? row.tuition_amount);
  let discountPercent = 0;
  let studentDiscount = 0;

  if (mode === "percentage") {
    discountPercent = toNumberSafe(value);
    studentDiscount = roundMoney((fee * discountPercent) / 100);
  } else {
    const fixedTotal = toNumberSafe(value);
    studentDiscount =
      totalCommissionableFee > 0
        ? roundMoney((fee / totalCommissionableFee) * fixedTotal)
        : 0;
    discountPercent = fee > 0 ? roundMoney((studentDiscount / fee) * 100) : 0;
  }

  const metrics = calculateCommissionDiscountMetrics({
    commissionableFee: fee,
    paidFee: toNumberSafe(row.studentPaidTuition ?? row.student_paid_tuition),
    discountPercent,
    expectedCommission: row.commissionAmount ?? row.commission_amount,
    receivedCommission: row.commissionReceived ?? row.commission_received,
    studentDiscount,
  });

  return {
    student_discount_percent: metrics.discountPercent,
    student_discount: metrics.studentDiscount,
    discount_paid: metrics.discountPaid,
    discount_balance: metrics.discountBalance,
    balance_fee: metrics.balanceFee,
    net_commission: metrics.netCommission,
    balance_commission: metrics.balanceCommission,
  };
}

function DiscountModeToggle({ value, disabled, onChange }) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-violet-200 bg-white p-0.5">
      {[
        { id: "percentage", label: "Percentage", icon: FaPercent },
        { id: "fixed", label: "Fixed amount", icon: FaDollarSign },
      ].map((option) => {
        const active = value === option.id;
        const Icon = option.icon;
        return (
          <button
            key={option.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.id)}
            className={`inline-flex h-7 items-center gap-1 rounded-md px-2.5 text-[10px] font-semibold transition ${
              active
                ? "bg-violet-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-violet-50"
            } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
            style={{ border: "none" }}
          >
            <Icon size={9} />
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function DiscountSavedView({ mode, value, amount, currency }) {
  const metaLine =
    mode === "percentage"
      ? [formatPercent(value, 2), "Percentage", currency].filter(Boolean).join(" · ")
      : ["Fixed amount", currency].filter(Boolean).join(" · ");

  return (
    <SetupCardSavedBody
      heroLabel="Student discount"
      heroAmount={formatCurrency(amount, currency)}
      metaLine={metaLine}
      tone="violet"
    />
  );
}

function CommissionDetailStudentDiscountCard({
  planContext,
  currency: currencyProp = "AUD",
  onSaved,
  onDraftChange,
}) {
  const savedPlan = planContext?.existing || null;
  const installments = Array.isArray(planContext?.installments)
    ? planContext.installments
    : [];
  const planLoading = Boolean(planContext?.loading);
  const currency =
    savedPlan?.currency || planContext?.defaults?.currency || currencyProp || "AUD";
  const commissionableFee = toNumberSafe(
    savedPlan?.commissionableTuitionFee ?? savedPlan?.commissionable_tuition_fee,
  );

  const derived = useMemo(
    () => deriveStudentDiscountSettings(savedPlan || {}),
    [savedPlan],
  );

  const [isEditing, setIsEditing] = useState(false);
  const [discountMode, setDiscountMode] = useState("percentage");
  const [discountValue, setDiscountValue] = useState("");
  const [saving, setSaving] = useState(false);

  const resetForm = useCallback(() => {
    setDiscountMode(derived.mode);
    setDiscountValue(derived.value);
  }, [derived]);

  useEffect(() => {
    setIsEditing(false);
    resetForm();
  }, [savedPlan?.id, derived.mode, derived.value, resetForm]);

  const editableInstallments = useMemo(
    () => installments.filter((row) => installmentIsEditable(row)),
    [installments],
  );

  const canSubmit =
    !saving &&
    savedPlan?.id &&
    discountValue !== "" &&
    toNumberSafe(discountValue) >= 0;

  const handleSave = async () => {
    if (!canSubmit) return;

    setSaving(true);
    try {
      const planPayload = buildStudentDiscountPlanPayload({
        discountMode,
        discountValue,
        commissionableTuitionFee: commissionableFee,
      });

      await updateNewCommissionPlan(savedPlan.id, planPayload);

      const totalCommissionableFee = installments.reduce(
        (sum, row) => sum + toNumberSafe(row.tuitionAmount ?? row.tuition_amount),
        0,
      );

      if (editableInstallments.length > 0) {
        await mapWithConcurrency(editableInstallments, (row) =>
          updateCommissionInstallment(row.id, {
            ...buildInstallmentDiscountPayload(row, savedPlan, {
              mode: discountMode,
              value: discountValue,
              totalCommissionableFee:
                totalCommissionableFee > 0
                  ? totalCommissionableFee
                  : commissionableFee,
            }),
          }),
        );
      }

      setIsEditing(false);
      Swal.fire({
        icon: "success",
        title: "Student discount saved",
        text: editableInstallments.length
          ? `Saved on plan and applied to ${editableInstallments.length} installment(s).`
          : "Saved on commission plan.",
        timer: 1400,
        showConfirmButton: false,
      });
      if (typeof onSaved === "function") await onSaved();
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to save student discount.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const hasSetting = Boolean(derived.value && toNumberSafe(derived.value) > 0);
  const showForm = isEditing || !hasSetting;

  useEffect(() => {
    if (!onDraftChange) return undefined;
    if (!showForm) {
      onDraftChange(null);
      return undefined;
    }
    const amount = previewStudentDiscountAmount(
      discountMode,
      discountValue,
      commissionableFee,
    );
    onDraftChange({
      studentDiscountType: discountMode,
      student_discount_type: discountMode,
      studentDiscountPercent:
        discountMode === "percentage" ? toNumberSafe(discountValue) : 0,
      student_discount_percent:
        discountMode === "percentage" ? toNumberSafe(discountValue) : 0,
      studentDiscountAmount: amount,
      student_discount_amount: amount,
    });
    return () => onDraftChange(null);
  }, [
    onDraftChange,
    showForm,
    discountMode,
    discountValue,
    commissionableFee,
  ]);

  return (
    <section
      className={`flex h-full flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition ${
        isEditing ? "border-violet-300" : "border-slate-200"
      }`}
    >
      <div className="flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-violet-50/80 to-white px-3 py-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-violet-600 text-white">
          <FaPercent size={11} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="mb-0 text-[13px] font-semibold text-slate-900">
              Student discount
            </h3>
            {!savedPlan ? (
              <Chip tone="amber">Setup</Chip>
            ) : hasSetting ? (
              <Chip tone={isEditing ? "amber" : "green"}>
                {isEditing ? "Editing" : derived.mode === "percentage" ? "% set" : "Fixed set"}
              </Chip>
            ) : (
              <Chip tone="amber">Not set</Chip>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {savedPlan && hasSetting && !isEditing ? (
            <button
              type="button"
              onClick={() => {
                resetForm();
                setIsEditing(true);
              }}
              className="inline-flex h-7 items-center gap-1 rounded-md bg-violet-600 px-2 text-[10px] font-semibold text-white hover:bg-violet-700"
              style={{ border: "none" }}
            >
              <FaEdit size={9} />
              Edit
            </button>
          ) : null}
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setIsEditing(false);
                }}
                disabled={saving}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
              >
                <FaTimes size={9} />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSubmit}
                className={`inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-[11px] font-semibold text-white ${
                  canSubmit
                    ? "bg-violet-600 hover:bg-violet-700"
                    : "cursor-not-allowed bg-violet-400/50"
                }`}
                style={{ border: "none" }}
              >
                <FaCheck size={9} />
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2.5 p-2.5">
        {planLoading && !savedPlan ? (
          <div className="flex flex-1 items-center justify-center py-6 text-[12px] text-slate-400">
            Loading…
          </div>
        ) : !savedPlan ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-violet-200 bg-violet-50/40 px-3 py-4 text-center text-[12px] text-slate-500">
            Create the commission plan first, then set student discount.
          </div>
        ) : !showForm ? (
          <DiscountSavedView
            mode={derived.mode}
            value={derived.value}
            amount={derived.amount}
            currency={currency}
          />
        ) : (
          <div className="flex flex-col gap-2.5">
            <DiscountModeToggle
              value={discountMode}
              disabled={saving}
              onChange={setDiscountMode}
            />

            <label className="block min-w-0">
              <FieldLabel
                hint={
                  discountMode === "percentage"
                    ? "Saved on plan"
                    : currency
                }
              >
                {discountMode === "percentage" ? "Discount %" : "Fixed amount"}
              </FieldLabel>
              <input
                type="number"
                min="0"
                step="0.01"
                value={discountValue}
                disabled={saving}
                onChange={(e) => setDiscountValue(e.target.value)}
                className={controlClass}
                placeholder="0"
              />
            </label>

            {!isEditing && hasSetting === false ? (
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSubmit}
                className={`inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-lg text-[13px] font-semibold text-white transition ${
                  canSubmit
                    ? "bg-violet-600 hover:bg-violet-700"
                    : "cursor-not-allowed bg-violet-400/50"
                }`}
                style={{ border: "none" }}
              >
                <FaCheck size={11} />
                {saving ? "Saving…" : "Save student discount"}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}

export default CommissionDetailStudentDiscountCard;
